import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardBody, CardText, CardTitle, Col, Container, Form, FormFeedback, Input, Label, Modal, Row } from 'reactstrap';
import * as Yup from "yup";

// import images
import logo from "../../assets/images/logo.png";
import CarouselPage from './CarouselPage';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from "react-redux";
import {
    forgetEmailVerified,
    otpVerified,
    otpVerifiedApiCall,
    passwordChangeRequest
} from "../../store/auth/forgetpwd/actions";
import { ToastWrapper } from "../../components/Common/CustomToast";
import { REGISTER_USER_INITIAL } from "../../store/auth/register/actionTypes";
import { FORGET_PASSWORD_INITIAL } from "../../store/auth/forgetpwd/actionTypes";
import { Button } from "../../components/ui/button";

const RecoverPassword = () => {
    //meta title
    document.title = "Reset Password";
    const [confirmEmail, setConfirmEmail] = useState("");
    const otpInputs = useRef([]);
    const [step, setStep] = useState(1);
    const dispatch = useDispatch();
    const { emailVerified, otpVerified, passwordChanged } = useSelector((state) => state?.ForgetPassword);
    const [passwordShow, setPasswordShow] = useState(false);
    const [confirmPasswordShow, setConfirmPasswordShow] = useState(false);
    const navidate = useNavigate();
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [redirectCountdown, setRedirectCountdown] = useState(10);
    const [isEmailSubmit, setIsEmailSubmit] = useState(false);
    const [isOtpSubmit, setIsOtpSubmit] = useState(false);
    const [isPasswordReset, setIsPasswordReset] = useState(false);
    const [timerStarted, setTimerStarted] = useState(false);
    const [seconds, setSeconds] = useState(0); // 1 minutes = 60 seconds
    const [canResend, setCanResend] = useState(false);

    useEffect(() => {
        let timer;
        if (seconds > 0) {
            timer = setTimeout(() => setSeconds((prev) => prev - 1), 1000);
        } else {
            setCanResend(true);
        }
        return () => clearTimeout(timer);
    }, [seconds]);

    useEffect(() => {
        if (timerStarted) {
            setCanResend(false);
            setSeconds(60)
        }
    }, [timerStarted]);

    const formatTime = (secs) => {
        const m = String(Math.floor(secs / 60)).padStart(2, '0');
        const s = String(secs % 60).padStart(2, '0');
        return `${m}:${s}`;
    };

    const handleChange = (value, index) => {
        if (!/^[0-9]?$/.test(value)) return;

        const newOtp = [...otpFormik.values.otp];
        newOtp[index] = value;
        otpFormik.setFieldValue("otp", newOtp);

        if (value && index < newOtp.length - 1) {
            otpInputs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        const paste = e.clipboardData.getData("text").trim();
        if (!/^\d+$/.test(paste)) return;
        const digits = paste.slice(0, 6).split("");
        const newOtp = [...otpFormik.values.otp];
        digits.forEach((digit, i) => {
            newOtp[i] = digit;
            if (otpInputs.current[i]) {
                otpInputs.current[i].value = digit;
            }
        });
        otpFormik.setFieldValue("otp", newOtp);
        otpInputs.current[digits.length - 1]?.focus();
        if (digits.length === 6) {
            otpFormik.handleSubmit();
          }
        e.preventDefault();
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace" && !otpFormik.values.otp[index] && index > 0) {
            otpInputs.current[index - 1]?.focus();
        }
    };

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            email: '',
        },
        validationSchema: Yup.object({
            email: Yup.string()
                .required("Please enter an email address")
                .max(100, 'Email must be 100 characters or less')
                .matches(/^\S+$/, 'Email must not contain spaces')
                .matches(/^[\x00-\x7F]+$/, 'Email must be ASCII only')
                .matches(/^[^<>]*$/, 'Invalid characters detected')
                .matches(
                    /^(?!\.)(?!.*\.\.)[A-Za-z0-9._%+-]+@(?!(?:[-]))[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/,
                    "Please enter a valid email address"
                ),
        }),
        onSubmit: (values) => {
            setConfirmEmail(values.email);
            const payload = {
                email: values.email,
                isResetOTP: true
            };
            dispatch(forgetEmailVerified(payload));
        }
    });

    const otpValidationSchema = Yup.object({
        otp: Yup.array()
            .of(Yup.string().matches(/^[0-9]$/, "Only numbers allowed"))
            .required("OTP is required")
            .test("complete-otp", "Enter complete OTP", (otp) =>
                otp.every((val) => val !== "")
            ),
    })

    const otpFormik = useFormik({
        initialValues: {
            otp: Array(6).fill(""),
        },
        validationSchema: otpValidationSchema,
        onSubmit: (values) => {
            setIsOtpSubmit(true);
            const payload = {
                email: values.email,
                otp: values.otp.join(""),
            };
            dispatch(otpVerifiedApiCall(payload));
        },
    });

    /*
    .min(8, "Password must be at least 8 characters")
            .matches(/[a-z]/, "Must include a lowercase letter")
            .matches(/[A-Z]/, "Must include an uppercase letter")
            .matches(/[0-9]/, "Must include a number")
            .matches(/[!@#$%^&*]/, "Must include a special character (!@#$%^&*)")
     */

    const passwordValidationSchema = Yup.object({
        password: Yup.string()
            .required("Password is required"),

        confirmPassword: Yup.string()
            .oneOf([Yup.ref("password"), null], "Passwords must match")
            .required("Confirm Password is required"),
    });

    const passwordChangeFormik = useFormik({
        enableReinitialize: true,
        initialValues: {
            password: "",
            confirmPassword: "",
        },
        validationSchema: passwordValidationSchema,
        onSubmit: (values) => {
            setIsPasswordReset(true);
            const payload = {
                email: values.email,
                password: values.password,
            };
            dispatch(passwordChangeRequest(payload));
        }
    })

    useEffect(() => {
        if (emailVerified === true && step === 1) {
            otpFormik.setFieldValue("email", confirmEmail);
            setStep(2);
        }
    }, [emailVerified]);

    useEffect(() => {
        if (otpVerified === true && step === 2) {
            setStep(3);
            passwordChangeFormik.setFieldValue("email", confirmEmail);
        }
    }, [otpVerified]);

    useEffect(() => {
        if (passwordChanged === true && step === 3) {
            setStep(1);
            setShowSuccessModal(true);
            const interval = setInterval(() => {
                setRedirectCountdown((prev) => {
                    if (prev <= 1) {
                        dispatch({ type: FORGET_PASSWORD_INITIAL });
                        clearInterval(interval);
                        setShowSuccessModal(false);
                        setStep(1);
                        navidate(`/login`, {});
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [passwordChanged]);

    const handleResend = () => {
        setCanResend(false);
        setSeconds(60); // reset to 1 minutes
        validation.handleSubmit();
    };

    return (
        <React.Fragment>
            <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
                <Card className="shadow p-4" style={{ minWidth: '460px', maxWidth: '460px' }}>
                    <CardBody>
                        <div id="welcome" className="mb-2 text-center" style={{ marginBottom: 0 }}>
                            <Link to="/dashboard" className="d-block auth-logo">
                                <img src={logo} alt="Logo" height="64" style={{
                                    display: "block",
                                    margin: "0 auto",
                                    padding: 0,
                                    lineHeight: 1,
                                    verticalAlign: "middle",
                                    border: "none",
                                    maxHeight: "64px",
                                }} />
                            </Link>
                        </div>
                        <CardTitle tag="h4" className="text-center mb-2">Reset Password 👋</CardTitle>
                        {step === 1 && (
                            <Form className="custom-form tf_common_form mt-4"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    setIsEmailSubmit(true);
                                    validation.handleSubmit();
                                    return false;
                                }}>
                                <div className="mb-3">
                                    <Label className="form-label">Email address</Label>
                                    <Input
                                        name="email"
                                        className="form-control"
                                        placeholder="Enter email address"
                                        type="email"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.email || ""}
                                        invalid={
                                            validation.touched.email && validation.errors.email ? true : false
                                        }
                                    />
                                    {validation.touched.email && validation.errors.email ? (
                                        <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                                    ) : null}
                                </div>
                                <div className="mb-3 mt-4">
                                    <button className={`btn btn-primary w-100 ${!validation.isValid ? 'disabled' : ''}`} type="submit" disabled={isEmailSubmit}>Reset Password</button>
                                </div>
                            </Form>
                        )}
                        {step === 2 && (
                            <div>
                                <div className="mb-3">
                                    <Label className="form-label">Enter OTP</Label>
                                    <div className="d-flex justify-content-between">
                                        {otpFormik.values.otp.map((digit, index) => (
                                            <input
                                                key={index}
                                                type="text"
                                                className="form-control text-center mx-1"
                                                style={{ width: "40px" }}
                                                maxLength="1"
                                                value={digit}
                                                onKeyDown={(e) => handleKeyDown(e, index)}
                                                onPaste={handlePaste}
                                                onChange={(e) => handleChange(e.target.value, index)}
                                                ref={(el) => (otpInputs.current[index] = el)}
                                            />
                                        ))}
                                    </div>
                                </div>
                                <div className="d-flex justify-content-center mt-3">
                                    {canResend ? (
                                        <Button
                                            variant="link"
                                            class="btn btn-link d-inline-flex align-items-center justify-content-center gap-2 text-primary text-decoration-underline text-nowrap rounded px-4 py-2 fw-medium small"
                                            onClick={handleResend}>
                                            Didn't receive the code? Resend
                                        </Button>
                                    ) : (
                                        <p className="text-gray-500">Resend OTP available in {formatTime(seconds)}</p>
                                    )}
                                </div>
                                <button className="btn btn-primary w-100 mt-3" disabled={isOtpSubmit} onClick={otpFormik.handleSubmit}>
                                    Verify OTP
                                </button>
                            </div>
                        )}
                        {step === 3 && (
                            <div>
                                <div className="row">
                                    <div className="col-3 mb-2">
                                        <label className="form-label">Email id<span
                                            className='required_star'>*</span></label>
                                    </div>
                                    <div className="col-9 mb-2">
                                        <label className="form-label">{confirmEmail}</label>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="mb-2">
                                        <div className="d-flex align-items-start">
                                            <div className="flex-grow-1">
                                                <Label className="form-label">Password</Label>
                                            </div>
                                        </div>
                                        <div id="enterPasswordArea" className="input-group auth-pass-inputgroup">
                                            <Input
                                                name="password"
                                                value={passwordChangeFormik.values.password || ""}
                                                type={passwordShow ? "text" : "password"}
                                                placeholder="Enter password"
                                                onChange={passwordChangeFormik.handleChange}
                                                onBlur={passwordChangeFormik.handleBlur}
                                                invalid={
                                                    passwordChangeFormik.touched.password && passwordChangeFormik.errors.password ? true : false
                                                }
                                            />
                                            <button onClick={() => setPasswordShow(!passwordShow)} className={`btn btn-light shadow-none ms-0 ${passwordShow ? 'show' : ''}`} type="button" id="onboarding-password-addon"></button>
                                            {passwordChangeFormik.touched.password && passwordChangeFormik.errors.password ? (
                                                <FormFeedback type="invalid">{passwordChangeFormik.errors.password}</FormFeedback>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="mb-2">
                                        <div className="d-flex align-items-start">
                                            <div className="flex-grow-1">
                                                <Label className="form-label">Confirm Password</Label>
                                            </div>
                                        </div>
                                        <div id="enterCPasswordArea" className="input-group auth-pass-inputgroup">
                                            <Input
                                                name="confirmPassword"
                                                value={passwordChangeFormik.values.confirmPassword || ""}
                                                type={confirmPasswordShow ? "text" : "password"}
                                                placeholder="Enter Confirm password"
                                                onChange={passwordChangeFormik.handleChange}
                                                onBlur={passwordChangeFormik.handleBlur}
                                                invalid={
                                                    passwordChangeFormik.touched.confirmPassword && passwordChangeFormik.errors.confirmPassword ? true : false
                                                }
                                            />
                                            <button onClick={() => setConfirmPasswordShow(!confirmPasswordShow)} className={`btn btn-light shadow-none ms-0 ${confirmPasswordShow ? 'show' : ''}`} type="button" id="onboarding-password-addon"></button>
                                            {passwordChangeFormik.touched.confirmPassword && passwordChangeFormik.errors.confirmPassword ? (
                                                <FormFeedback type="invalid">{passwordChangeFormik.errors.confirmPassword}</FormFeedback>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>

                                <button className="btn btn-primary w-100" disabled={isPasswordReset} onClick={passwordChangeFormik.handleSubmit}>Submit</button>
                            </div>
                        )}
                        <div className='d-flex justify-content-center align-items-center mt-3'>
                            <p className="text-muted mb-0">Remember It ?  <Link to="/login"
                                className="text-primary fw-semibold"> Sign In </Link> </p>
                        </div>
                    </CardBody>
                </Card>
            </div>
            <Modal size="sm" isOpen={showSuccessModal} toggle={() => setShowSuccessModal(!showSuccessModal)} centered backdrop="static">
                <div className="modal-body text-center">
                    <img
                        src="https://t4.ftcdn.net/jpg/05/17/53/57/360_F_517535712_q7f9QC9X6TQxWi6xYZZbMmw5cnLMr279.jpg"
                        alt="Register Success"
                        style={{ width: "100px", height: "100px", objectFit: "cover" }}
                        className="mb-3" />

                    <div className="alert alert-success d-inline-flex align-items-center justify-content-center w-100 mb-0" role="alert">
                        <i className="bi bi-check-circle-fill me-2"></i>
                        <span>
                            Redirecting in <strong>{redirectCountdown}</strong> second{redirectCountdown !== 1 ? "s" : ""}...
                        </span>
                    </div>
                </div>
            </Modal>
            <ToastWrapper />
        </React.Fragment>
    );
};

export default RecoverPassword;
